/* @jsx jsx */

/* @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import Retracked from 'js/app/retracked';
import helpcenter from 'js/lib/courseraHelpcenter';
import isMobileApp from 'js/lib/isMobileApp';
import user from 'js/lib/user';

import useIsMobile from 'bundles/common/hooks/useIsMobile';
import { previewUserAdminEducator } from 'bundles/common/utils/globalHelpButtonUtils';
import Icon from 'bundles/iconfont/Icon';
import TrackedButton from 'bundles/page/components/TrackedButton';
import { TrackedA } from 'bundles/page/components/TrackedLink2';

/* eslint-enable import/extensions */
import _t from 'i18n!nls/alice';

import 'css!./__styles__/Help';

type Props = {
  style?: { [styleAttr: string]: string | number };
  isEnterprise?: boolean;
  /* This component used to only use the trackingName 'icon'. Under the Access to Support data science experiments we now have this help icon shown at the bottom right of almost every page. To measure effectiveness of the more global help icon we need a separate trackingName: 'global_icon' */
  trackingName?: 'icon' | 'global_icon';
  isAdminPage?: boolean;
  isEducatorPage?: boolean;
};

// These styles were migrated from the old Help.styl file.
// The migration is only partially completed: the Help Link button below continues to use the old styles.
const styles = {
  helpIconWrapper: css`
    font-size: 24px;
    color: var(--cds-color-blue-600);
    background-color: var(--cds-color-neutral-background-primary);
    width: 24px;
    height: 24px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;

    [dir='rtl'] & {
      -webkit-transform: scaleX(-1);
      -moz-transform: scaleX(-1);
      -o-transform: scaleX(-1);
      transform: scaleX(-1);
      filter: fliph;
      -ms-filter: 'fliph';
    }
  `,
  helpButton: css`
    border: none;
    background-color: transparent;
    padding: 0;
    min-width: 0;
    bottom: 22px;
    right: 22px;
    position: fixed;
    z-index: 1000;
  `,
};

const getHelpCenterText = () => {
  return _t('Help Center');
};

const Help = ({ style, isEnterprise, trackingName = 'icon', isAdminPage = false, isEducatorPage = false }: Props) => {
  const tooltip = getHelpCenterText();

  const isMobile = useIsMobile();

  if (isMobileApp.get() || isMobile) {
    return null;
  }
  const isAuthenticatedUser = user.isAuthenticatedUser();
  const link = isEnterprise
    ? helpcenter.getEnterpriseHelpLink(isAuthenticatedUser)
    : helpcenter.getNewHelpCenterHome(isAuthenticatedUser);

  let helpButtonLink = helpcenter.getNewHelpCenterHome(isAuthenticatedUser);
  if (isAdminPage) {
    helpButtonLink = 'https://business.coursera.help';
  } else if (isEducatorPage) {
    helpButtonLink = 'https://partner.coursera.help';
  } else if (isEnterprise) {
    helpButtonLink = helpcenter.getEnterpriseHelpLink(isAuthenticatedUser);
  }

  let helpButtonTrackingName = 'global_help_icon';
  if (isAdminPage) {
    helpButtonTrackingName = 'global_help_icon_admin';
  } else if (isEducatorPage) {
    helpButtonTrackingName = 'global_help_icon_educator';
  }

  // Regular link to help center that is not part of Access to Support experiments
  const HelpLink = (
    <TrackedA
      className="rc-Help link-button nostyle mui-fixed"
      trackingName={trackingName}
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={tooltip}
      title={tooltip}
      style={style || { minWidth: 0, bottom: 20, right: 20 }}
    >
      <div className="help-widget horizontal-box align-items-absolute-center">
        <Icon name="question-circle-o" />
      </div>
    </TrackedA>
  );

  /* There is one version of the HelpButton:
  1. The user is on a valid learner, admin, or educator page where we show global help button - onClick links to their respective Help Center */
  const HelpButton = (
    <>
      <TrackedButton
        trackingName={helpButtonTrackingName}
        title={tooltip}
        aria-label={tooltip}
        data={{ isAdminPage, isEducatorPage }}
        onClick={(e) => {
          e.stopPropagation();
          window.open(helpButtonLink);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
        }}
        className="rc-Help mui-fixed"
        css={styles.helpButton}
      >
        <div css={styles.helpIconWrapper}>
          <Icon name="question-circle-o" />
        </div>
      </TrackedButton>
    </>
  );

  const isAccessToSupport = trackingName === 'global_icon';
  return isAccessToSupport ? HelpButton : HelpLink;
};

export default Retracked.createTrackedContainer<Props>(() => {
  const { isAdmin, isEducator } = previewUserAdminEducator();
  return {
    namespace: { page: 'help' },
    isAdmin,
    isEducator,
    currentPath: typeof window !== 'undefined' ? window.location.pathname : undefined,
  };
})(Help);
